/**
 * Shared session row chrome for the Projects agent.
 *
 * Used by ``SessionPickerList`` (interactive) and the Answers card
 * (read-only, with a loading / unavailable state). Keeping one row
 * component stops the picker and the post-selection summary from
 * drifting visually.
 */

import { cn } from '@heidi/utils';
import { FileText, Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { sharedAgentMessages } from '../../../shared.messages';

export type SessionMetaRowStatus = 'ready' | 'loading' | 'unavailable';

interface SessionMetaRowContentProps {
  sessionName?: string;
  patientName?: string;
  status?: SessionMetaRowStatus;
  /** Highlight as selected (picker). */
  selected?: boolean;
  /**
   * Show a border around the row. Defaults to `true`.
   * Pass `false` when the row already lives inside a bordered container
   * (e.g. the Answers card) to avoid a double-border.
   */
  bordered?: boolean;
  /** Trailing affordance (e.g. checkmark). Hidden while loading. */
  trailing?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

type SessionMetaRowButtonProps = SessionMetaRowContentProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
    as?: 'button';
  };

type SessionMetaRowDivProps = SessionMetaRowContentProps & {
  as: 'div';
};

export type SessionMetaRowProps = SessionMetaRowButtonProps | SessionMetaRowDivProps;

const CONTENT_KEYS = [
  'as',
  'sessionName',
  'patientName',
  'status',
  'selected',
  'bordered',
  'trailing',
  'className',
  'data-testid',
] as const;

const omitContentProps = (
  props: SessionMetaRowButtonProps
): Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> => {
  const rest = { ...props } as Record<string, unknown>;
  for (const key of CONTENT_KEYS) {
    delete rest[key];
  }
  return rest;
};

const rowChrome = (
  status: SessionMetaRowStatus,
  selected: boolean,
  interactive: boolean,
  bordered: boolean
) =>
  cn(
    'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left',
    bordered && 'border',
    interactive && 'transition-colors disabled:cursor-not-allowed disabled:opacity-60',
    selected
      ? 'border-border-pressed bg-background-secondary'
      : bordered
        ? 'border-border bg-surface'
        : 'bg-surface',
    interactive && !selected && 'hover:bg-surface-hover',
    status === 'unavailable' && 'opacity-80'
  );

const SessionMetaRowBody = ({
  status,
  sessionName,
  patientName,
  trailing,
  loadingLabel,
  unavailableLabel,
  untitledLabel,
}: {
  status: SessionMetaRowStatus;
  sessionName?: string;
  patientName?: string;
  trailing?: ReactNode;
  loadingLabel: string;
  unavailableLabel: string;
  untitledLabel: string;
}) => {
  if (status === 'loading') {
    return (
      <>
        <Loader2
          className="size-4 shrink-0 animate-spin text-text-secondary"
          aria-hidden
        />
        <span className="flex min-w-0 flex-1 flex-col gap-1.5" aria-hidden>
          <span className="h-3.5 w-2/3 max-w-48 animate-pulse rounded bg-background-secondary" />
          <span className="h-3 w-1/3 max-w-28 animate-pulse rounded bg-background-secondary" />
        </span>
        <span className="sr-only">{loadingLabel}</span>
      </>
    );
  }

  // Ignore session/patient names on unavailable — names can carry PHI and
  // callers must not accidentally surface them for a revoked/deleted session.
  if (status === 'unavailable') {
    return (
      <>
        <FileText className="size-5 shrink-0 text-text-secondary" aria-hidden />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm text-text-primary">{unavailableLabel}</span>
        </span>
        {trailing}
      </>
    );
  }

  const title = sessionName?.trim() || untitledLabel;

  return (
    <>
      <FileText className="size-5 shrink-0 text-text-secondary" aria-hidden />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm text-text-primary">{title}</span>
        {patientName?.trim() && (
          <span className="truncate text-xs text-text-tertiary">
            {patientName.trim()}
          </span>
        )}
      </span>
      {trailing}
    </>
  );
};

export const SessionMetaRow = (props: SessionMetaRowProps): JSX.Element => {
  const intl = useIntl();
  const status = props.status ?? 'ready';
  const selected = props.selected ?? false;
  const bordered = props.bordered ?? true;
  const dataTestId = props['data-testid'] ?? 'session-meta-row';
  const loadingLabel = intl.formatMessage({
    id: 'IY7S4mLrkm',
    defaultMessage: 'Loading session',
  });
  const unavailableLabel = intl.formatMessage({
    id: 'L0004MtYAd',
    defaultMessage: 'Session unavailable',
  });
  const untitledLabel = intl.formatMessage(sharedAgentMessages.untitledSession);

  const body = (
    <SessionMetaRowBody
      status={status}
      sessionName={props.sessionName}
      patientName={props.patientName}
      trailing={status === 'loading' ? undefined : props.trailing}
      loadingLabel={loadingLabel}
      unavailableLabel={unavailableLabel}
      untitledLabel={untitledLabel}
    />
  );

  if (props.as === 'div') {
    return (
      <div
        className={cn(rowChrome(status, selected, false, bordered), props.className)}
        data-testid={dataTestId}
        data-status={status}
        aria-busy={status === 'loading' || undefined}
      >
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      {...omitContentProps(props)}
      className={cn(rowChrome(status, selected, true, bordered), props.className)}
      data-testid={dataTestId}
      data-status={status}
      aria-busy={status === 'loading' || undefined}
    >
      {body}
    </button>
  );
};
